import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';

export interface PopoverProps {
  /** Renders the trigger; spread `props` onto a `<button>`. */
  trigger: (props: {
    'aria-haspopup': 'dialog';
    'aria-expanded': boolean;
    'aria-controls': string;
    onClick: () => void;
  }) => ReactNode;
  /** The panel's content; call `close` when it is done. */
  children: (close: () => void) => ReactNode;
  align?: 'start' | 'end';
  ariaLabel: string;
  panelClassName?: string;
}

/**
 * A small panel anchored under its trigger — for a quick edit in place, where
 * a modal would be more ceremony than the change deserves. Closes on outside
 * click or Escape. For a list of actions use `DropdownMenu`.
 */
export function Popover({
  trigger,
  children,
  align = 'start',
  ariaLabel,
  panelClassName = 'w-80',
}: PopoverProps) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open, close]);

  return (
    <div
      ref={containerRef}
      className="relative inline-flex"
      onKeyDown={(e) => {
        if (e.key === 'Escape' && open) {
          e.stopPropagation();
          close();
        }
      }}
    >
      {trigger({
        'aria-haspopup': 'dialog',
        'aria-expanded': open,
        'aria-controls': panelId,
        onClick: () => setOpen((o) => !o),
      })}
      {open && (
        <div
          id={panelId}
          role="dialog"
          aria-label={ariaLabel}
          className={`absolute top-full mt-1.5 z-40 bg-white border border-gray-200 rounded-xl shadow-lg p-4 animate-menu-in max-w-[calc(100vw-2rem)] ${
            align === 'end' ? 'right-0' : 'left-0'
          } ${panelClassName}`}
        >
          {children(close)}
        </div>
      )}
    </div>
  );
}
